import { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { cn } from '@divlab/divanui';

import InputFocusStore from '@Stores/InputFocus/InputFocusStore';
import useSellPoints from '@Queries/useSellPoints';
import useMedias from '@Hooks/useMedias';
import { useRequest } from '@Contexts/Request';
import { useAppConfig } from '@Contexts/AppConfig';
import { useComponentStorage } from '@Stores/ComponentStorage';
import { useInputFocuse } from '@Stores/InputFocus';
import PageUp from '@Components/PageUp';
import BottomUserMenu from '../BottomUserMenu';
import Messengers from '../Messengers';
import DevPanel from '@Components/DevPanel';
import styles from './BottomPanel.module.css';

import type { FC, HTMLAttributes } from 'react';

export interface BottomPanelProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const BottomPanel: FC<BottomPanelProps> = () => {
  const { isMobile, isMobileM } = useMedias();
  const components = useComponentStorage();
  const { country } = useRequest();
  const refPanel = useRef<HTMLDivElement>(null);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);
  const { data: sellPoints, isPending } = useSellPoints();
  const isBLR = country === 'BLR';
  const { setIsFocused } = InputFocusStore;
  const { inputIsFocused } = useInputFocuse();
  const isVisibleAllMessengers = inputIsFocused
    ? !inputIsFocused || !isMobile
    : !isMobile || !isBLR;

  const { isProduction } = useAppConfig();

  const isComponentVisible = useMemo(() => {
    const possibleComponents = Object.values(components).filter((component) => component !== null);

    if (possibleComponents.length) return possibleComponents[0].props?.visible;

    return false;
  }, [components]);

  const handleResize = useCallback(() => {
    if (!refPanel.current) return;

    setPlaceholderHeight(refPanel.current.getBoundingClientRect().height);
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    const handleFocus = (event) => {
      const target = event.target;

      if (!target || target.type === 'checkbox' || target.tagName !== 'INPUT') {
        return;
      }
      setIsFocused(true);
    };

    const handleBlur = (event) => {
      const target = event.target;
      if (!target || target.type === 'checkbox' || target.tagName !== 'INPUT') {
        return;
      }
      setIsFocused(false);
    };

    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);

    return () => {
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleBlur, true);
    };
  }, [setIsFocused]);

  const stickyContent = useMemo(() => {
    if (!components) return null;

    if (components.buyButton && isMobileM) return components.buyButton;
    if (components.orderTextile) return components.orderTextile;
    if (components.orderButton && isMobileM) return components.orderButton;
    if (components.saleSoon && isMobileM) return components.saleSoon;

    return null;
  }, [components, isMobileM]);

  return (
    <>
      <div
        className={cn(
          styles.wrapper,
          { [styles.focused]: inputIsFocused },
          { [styles.hidden]: isPending },
        )}
        ref={refPanel}
      >
        <div
          className={cn(styles.panel, {
            [styles.expanded]: isComponentVisible,
          })}
        >
          <div className={styles.buttons}>
            <PageUp
              className={cn(styles.up, { [styles.withMessangers]: isVisibleAllMessengers })}
            />

            {!isProduction && <DevPanel className={styles.devPanel} />}

            {isVisibleAllMessengers && <Messengers />}
          </div>
          <div className={styles.content}>{stickyContent}</div>
        </div>
        {isMobileM && (
          <BottomUserMenu propSellPoint={sellPoints?.sellPoints} className={styles.menu} />
        )}
      </div>
      {isMobileM && <div style={{ height: `${placeholderHeight}px` }} />}
    </>
  );
};

export default BottomPanel;
